/**
 * Centralized, editable site content.
 *
 * Everything in here is brand/editorial copy that is expected to change at
 * (or before) launch. Keeping it in one place means no more hunting through
 * JSX for placeholder names, dead social links, or hardcoded league lists.
 *
 * Defaults are intentionally neutral/representative — swap the real values in
 * when they exist. A social link with an empty `url` renders as "coming soon"
 * rather than a dead `href="#"`.
 */

export interface SocialLink {
  /** Platform name, used for the accessible label. */
  platform: 'instagram' | 'tiktok' | 'x';
  /** Public-facing handle, e.g. "@2girls1parlay". Optional. */
  handle?: string;
  /** Full profile URL. Leave empty to render a disabled "coming soon" state. */
  url?: string;
}

export interface SiteConfig {
  /** Leagues the publication covers, shown in the masthead and copy. */
  leagues: string[];
  /** People credited as founders/editors in the colophon. */
  founders: string[];
  /** Default byline used for blog posts that don't set their own author. */
  defaultAuthor: string;
  /** Social profiles. Empty `url` => disabled "coming soon" affordance. */
  socials: SocialLink[];
  /** Public contact address. Fill in before deploy. */
  contactEmail: string;
}

export const siteConfig: SiteConfig = {
  leagues: ['NBA', 'MLB', 'NFL', 'NHL'],
  founders: ['Frankie', 'Sloane'],
  defaultAuthor: 'Sloane',
  // Instagram is the only live channel for now. Add tiktok/x back here when
  // (if) those pages actually exist — an empty `url` renders "coming soon".
  socials: [
    { platform: 'instagram', handle: '@2girls_1parlay', url: 'https://www.instagram.com/2girls_1parlay/' },
  ],
  contactEmail: 'contact@2girls1parlay.com',
};
